import React, { useMemo, useState } from 'react';
import StackedProgressBar from './StackedProgressBar';
import './DeNovoView.css';
import { subjectMap } from '../constants';

const DeNovoStatsPage = ({ deNovoData, onBack, allRulesBySubject }) => { // <--- Added prop here
  const stats = useMemo(() => {
    const vals = Object.values(deNovoData || {});
    return {
      total: vals.length,
      learning: vals.filter(d => d.srs_data?.learningStepIndex !== -1).length,
      graduated: vals.filter(d => d.srs_data?.learningStepIndex === -1 && d.srs_data?.lastReviewedDate).length,
      leech: vals.filter(d => d.srs_data?.isLeech).length
    };
  }, [deNovoData]);

  return (
    <div className="stats-page-wrapper theme-aware">
        <div className="home-screen-content wide-content">
            <h1 className="home-title">Retention Intelligence</h1>
            
            <div className="stats-grid-container">
                <div className="stats-card">
                    <div className="stats-card-value">{stats.total}</div>
                    <div className="stats-card-label">Total Active Cards</div>
                </div>
                <div className="stats-card">
                    <div className="stats-card-value" style={{ color: 'var(--flagged-color)' }}>{stats.learning}</div>
                    <div className="stats-card-label">Short Term Memory</div>
                </div>
                <div className="stats-card">
                    <div className="stats-card-value" style={{ color: 'var(--success-color)' }}>{stats.graduated}</div>
                    <div className="stats-card-label">Long Term Memory</div>
                </div>
                <div className="stats-card">
                    <div className="stats-card-value" style={{ color: 'var(--error-color)' }}>{stats.leech}</div>
                    <div className="stats-card-label">Critical Failures (Leeches)</div>
                </div>
            </div>

            {/* PASSING allRulesBySubject DOWN */}
            <MemoryHorizonChart deNovoData={deNovoData} allRulesBySubject={allRulesBySubject} />

            <button onClick={onBack} className="text-link" style={{marginTop: '40px'}}>
                <span className="material-symbols-outlined icon-sm">arrow_back</span>
                Back to De Novo Home
            </button>
        </div>
    </div>
  );
};

const DeckCard = ({ deck, onContinue, onReset, onDelete, onRename }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(deck.deck_name);

    const progress = deck.progress?.completed_ids?.length || 0;
    const total = deck.rule_ids.length;
    const percent = total > 0 ? (progress / total) * 100 : 0;
    
    const handleRename = () => {
        if (newName.trim() && newName !== deck.deck_name) {
            onRename(deck.id, newName.trim());
        }
        setIsEditing(false);
        setIsMenuOpen(false);
    };

    return (
        <div className="deck-card">
            <div className="deck-info">
                {isEditing ? (
                    <input 
                        type="text" 
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onBlur={handleRename}
                        onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                        className="deck-rename-input"
                        autoFocus
                    />
                ) : (
                    <h4 className="deck-name">{deck.deck_name}</h4>
                )}
                <div className="deck-meta">
                    <span>{new Date(deck.created_at).toLocaleDateString()}</span>
                    {deck.last_studied_at && <span>Last studied: {new Date(deck.last_studied_at).toLocaleDateString()}</span>}
                </div>
            </div>
            <div className="deck-progress">
                <div className="progress-text">{progress} / {total}</div>
                <div className="progress-track-deck"><div className="progress-fill-deck" style={{width: `${percent}%`}}></div></div>
            </div>
            <div className="deck-actions">
                <button className="btn continue-btn" onClick={() => onContinue(deck.id)} disabled={progress === total}>
                    {progress === 0 ? "Start" : progress === total ? "Done" : "Continue"}
                </button>
                <div className="deck-menu-container">
                    <button className="icon-btn menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                        <span className="material-symbols-outlined">more_vert</span>
                    </button>
                    {isMenuOpen && (
                        <div className="deck-menu">
                            <button onClick={() => { setIsEditing(true); }}><span className="material-symbols-outlined">edit</span>Rename</button>
                            <button onClick={() => { onReset(deck.id); setIsMenuOpen(false); }}><span className="material-symbols-outlined">restart_alt</span>Reset</button>
                            <button className="danger" onClick={() => { onDelete(deck.id); setIsMenuOpen(false); }}><span className="material-symbols-outlined">delete</span>Delete</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MemoryHorizonChart = ({ deNovoData, allRulesBySubject }) => {
    
    // Helper to find subject of a rule ID (since deNovoData is flat)
    // We create a lookup map once for performance
    const ruleSubjectMap = useMemo(() => {
        const map = new Map();
        Object.entries(allRulesBySubject).forEach(([subj, rules]) => {
            rules.forEach(r => map.set(r.id, subj));
        });
        return map;
    }, [allRulesBySubject]);

    const chartData = useMemo(() => {
        if (!deNovoData) return [];

        // 1. Define Granular Buckets
        const buckets = [
            { id: 0, label: 'Critical', sub: 'Learning', min: -1, max: 0, count: 0, leeches: 0, subjects: {} },
            { id: 1, label: 'Fragile', sub: '1-3 Days', min: 1, max: 3, count: 0, leeches: 0, subjects: {} },
            { id: 2, label: 'Developing', sub: '4-7 Days', min: 4, max: 7, count: 0, leeches: 0, subjects: {} },
            { id: 3, label: 'Stable', sub: '8-14 Days', min: 8, max: 14, count: 0, leeches: 0, subjects: {} },
            { id: 4, label: 'Strong', sub: '15-30 Days', min: 15, max: 30, count: 0, leeches: 0, subjects: {} },
            { id: 5, label: 'Robust', sub: '1-2 Mos', min: 31, max: 60, count: 0, leeches: 0, subjects: {} },
            { id: 6, label: 'The Vault', sub: '60+ Days', min: 61, max: 9999, count: 0, leeches: 0, subjects: {} },
        ];

        // 2. Aggregate Data
        Object.entries(deNovoData).forEach(([ruleId, item]) => {
            const srs = item.srs_data;
            if (!srs) return;

            let bucketIndex = 0;

            // Determine Bucket
            if (srs.learningStepIndex !== -1 || !srs.lastReviewedDate) {
                bucketIndex = 0; // Learning Phase
            } else {
                const ivl = srs.currentInterval || 0;
                if (ivl <= 3) bucketIndex = 1;
                else if (ivl <= 7) bucketIndex = 2;
                else if (ivl <= 14) bucketIndex = 3;
                else if (ivl <= 30) bucketIndex = 4;
                else if (ivl <= 60) bucketIndex = 5;
                else bucketIndex = 6;
            }

            // Increment Count
            buckets[bucketIndex].count++;

            // Track Leeches
            if (srs.isLeech || srs.lapses > 3) {
                buckets[bucketIndex].leeches++;
            }

            // Track Subject Distribution
            const subj = ruleSubjectMap.get(ruleId) || 'unknown';
            buckets[bucketIndex].subjects[subj] = (buckets[bucketIndex].subjects[subj] || 0) + 1;
        });

        // 3. Post-Process (Scaling & Dominant Subject)
        const maxCount = Math.max(...buckets.map(b => b.count), 1);

        return buckets.map(b => {
            // Find dominant subject
            let topSubj = 'N/A';
            let topCount = 0;
            Object.entries(b.subjects).forEach(([s, c]) => {
                if (c > topCount) { topCount = c; topSubj = s; }
            });

            return {
                ...b,
                heightPct: (b.count / maxCount) * 100,
                leechPct: b.count > 0 ? (b.leeches / b.count) * 100 : 0,
                topSubject: subjectMap[topSubj] || 'Mixed'
            };
        });
    }, [deNovoData, ruleSubjectMap]);

    const totalCards = chartData.reduce((acc, curr) => acc + curr.count, 0);

    if (totalCards === 0) return null; // Or empty state

    return (
        <div className="stats-chart-section">
            <div className="chart-header">
                <h3>The Forgetting Horizon</h3>
                <p>Visualizing the stability of your legal knowledge. "Leech" segments (striped) indicate rules you frequently forget within that timeframe.</p>
            </div>
            
            <div className="memory-horizon-chart">
                {chartData.map((bucket, idx) => (
                    <div key={idx} className={`chart-column phase-${idx}`}>
                        
                        {/* The Hover Tooltip */}
                        <div className="chart-tooltip">
                            <div className="tooltip-header">
                                <span>{bucket.label}</span>
                                <span>{bucket.count} Rules</span>
                            </div>
                            <div className="tooltip-row">
                                <span>Range:</span>
                                <span>{bucket.sub}</span>
                            </div>
                            <div className="tooltip-row">
                                <span>Focus:</span>
                                <span style={{textTransform:'capitalize'}}>{bucket.topSubject}</span>
                            </div>
                            {bucket.leeches > 0 && (
                                <div className="tooltip-row leech-row">
                                    <span>Leeches:</span>
                                    <span>{bucket.leeches} ({Math.round(bucket.leechPct)}%)</span>
                                </div>
                            )}
                        </div>

                        {/* The Floating Count Label */}
                        <div className="bar-total-label">{bucket.count}</div>

                        {/* The Bar */}
                        <div className="bar-wrapper" style={{ height: `${Math.max(bucket.heightPct, 2)}%` }}>
                            {/* Leech Overlay inside the bar */}
                            <div 
                                className="leech-segment" 
                                style={{ height: `${bucket.leechPct}%` }}
                            ></div>
                        </div>

                        {/* Axis Labels */}
                        <div className="axis-label">
                            {bucket.label}
                            <span className="axis-sub-label">{bucket.sub}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};



const DeNovoHomeScreen = ({ 
    onStartPhase, 
    onCreateCustomSession, 
    deNovoData,
    dailyDueCount,
    cramDecks = [],
    onStartCramDeck,
    onDeleteCramDeck,
    onResetCramDeck,
    onRenameCramDeck,
    allRulesBySubject
}) => {
    const [showStats, setShowStats] = useState(false);
    const isLocked = false; 

    if (showStats) {
        return <DeNovoStatsPage deNovoData={deNovoData} allRulesBySubject={allRulesBySubject} onBack={() => setShowStats(false)} />;return <DeNovoStatsPage deNovoData={deNovoData} onBack={() => setShowStats(false)} />;
    }

    return ( 
        <div className="de-novo-home-wrapper theme-aware">
            <div className="home-screen-content wide-content">
                <div className="home-header-group">
                    <h1 className="home-title">De Novo&trade; Review</h1>
                    <p className="home-subtitle">"Because questions of law are reviewed <em>de novo</em>."</p>
                </div>
                
                <div className="dashboard-card home-card">
                    <div className="status-indicator-row">
                        <span className={`status-dot ${isLocked ? 'locked' : dailyDueCount > 0 ? 'active' : 'idle'}`}></span>
                        <span className="status-label">DAILY SRS QUEUE: {isLocked ? 'LOCKED' : dailyDueCount > 0 ? 'ACTIVE' : 'STANDBY'}</span>
                    </div>
                    <p className="queue-status-text">
                        {isLocked ? "Complete your Learning Phase to unlock afternoon review." :
                         dailyDueCount > 0 ? <span className="queue-count">{dailyDueCount} <span className="unit">Cards Due</span></span> :
                         "All caught up. Your retention is optimal."}
                    </p>
                    <button className="big-start-btn" onClick={() => !isLocked && onStartPhase('REVIEW')} disabled={isLocked || dailyDueCount === 0}>
                        {isLocked ? "SESSION LOCKED" : dailyDueCount > 0 ? "START DAILY SESSION" : "QUEUE EMPTY"}
                    </button>
                </div>`

                <div className="custom-decks-section">
                    <div className="decks-header">
                        <div className="decks-header-text">
                            <h3>Custom Decks</h3>
                            <p>Targeted cram sessions that don't affect your SRS progress.</p>
                        </div>
                        <div className="decks-header-actions">
                            <button className="text-link" onClick={() => setShowStats(true)}>
                                <span className="material-symbols-outlined">bar_chart</span> Stats
                            </button>
                            <button className="text-link" onClick={() => onCreateCustomSession()}>
                                + New Deck
                            </button>
                        </div>
                    </div>
                    {cramDecks.length > 0 ? (
                        <div className="decks-grid">
                            {cramDecks.map(deck => (
                                <DeckCard 
                                    key={deck.id} 
                                    deck={deck} 
                                    onContinue={onStartCramDeck}
                                    onDelete={onDeleteCramDeck}
                                    onReset={onResetCramDeck}
                                    onRename={onRenameCramDeck}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-decks-state">
                            <span className="material-symbols-outlined empty-icon">add_circle</span>
                            <h4>No Cram Decks Yet</h4>
                            <p>Create a custom deck to drill a specific subject or topic without impacting your main learning schedule.</p>
                            <button className="btn" onClick={onCreateCustomSession}>Create First Deck</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeNovoHomeScreen;